import { useCallback, useEffect, useState } from "react";
import { PhoneIncoming, Loader2, Save, Eye, EyeOff, Database, Target, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";

const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" role="switch" aria-checked={checked} data-testid={testid} onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-primary" : "bg-secondary"}`}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

// Contesto → "Agente vocale": conoscenze dell'assistente telefonico (solo admin). Tutto finisce nel prompt Vapi.
export const VoiceAgentConfig = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/voice/agent-config");
      setData(data);
      setForm(data.config);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/voice/agent-config", form);
      setData(data);
      setForm(data.config);
      toast.success(data.assistant_id ? "Conoscenze salvate e applicate all'agente vocale" : "Conoscenze salvate (l'agente verrà aggiornato al prossimo setup)");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data || !form) return null;
  const dirty = JSON.stringify(form) !== JSON.stringify(data.config);
  const kb = data.info?.kb;

  return (
    <section data-testid="voice-agent-config" className="surface space-y-5 p-6" style={{ borderColor: "rgba(245,185,66,0.35)" }}>
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#F5B942]/15 text-[#FCD990]"><PhoneIncoming className="h-5 w-5" /></span>
        <div>
          <p className="text-xs font-mono uppercase tracking-wide text-[#FCD990]">Agente vocale — chiamate in entrata e in uscita</p>
          <h2 className="text-base font-semibold md:text-lg">Cosa deve sapere l'assistente telefonico</h2>
          <p className="mt-1 text-sm text-muted-foreground">Quando qualcuno chiama il numero (o l'agente chiama), risponde usando queste conoscenze. Vengono inserite direttamente nel suo prompt: nessun ritardo in chiamata.</p>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Istruzioni e informazioni aziendali</label>
        <textarea data-testid="agent-istruzioni-input" value={form.istruzioni} onChange={(e) => setForm((f) => ({ ...f, istruzioni: e.target.value }))} rows={8} maxLength={12000}
          placeholder={"Es. Siamo Rossi Impianti, installiamo climatizzatori a Milano e provincia dal 1998.\nOrari: lun-ven 9-18. Sopralluogo gratuito entro 48h. Preventivi da 1.200€ per un monosplit.\nSe chiedono assistenza su un impianto esistente: raccogli nome, indirizzo e problema, verrà richiamato da un tecnico.\nNon promettere mai date di installazione precise."}
          className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm leading-relaxed outline-none focus:border-primary" />
        <p className="mt-1 text-right text-[11px] text-muted-foreground">{form.istruzioni.length.toLocaleString("it-IT")} / 12.000</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-4">
          <div className="flex gap-3">
            <Target className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
            <div>
              <p className="text-sm font-medium">Usa il contesto di vendita attivo</p>
              <p className="text-xs text-muted-foreground">{data.info?.contesto_presente ? "Settore, prodotto, cliente tipo e tono del contesto attivo." : "Nessun contesto attivo: attivane uno qui sopra."}</p>
            </div>
          </div>
          <Toggle testid="agent-usa-contesto" checked={form.usa_contesto} onChange={(v) => setForm((f) => ({ ...f, usa_contesto: v }))} />
        </div>
        <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-4">
          <div className="flex gap-3">
            <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium">Usa la Knowledge Base</p>
              <p className="text-xs text-muted-foreground">
                Script, linee guida e frasi pronte (anche dai file e manuali caricati).
                {kb && <> {kb.inclusi} frasi incluse su {kb.cluster_totali} conoscenze{kb.troncato && <span className="text-amber-300"> — limite raggiunto, priorità agli script</span>}.</>}
              </p>
              {form.usa_kb && (
                <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">Limite KB
                  <select data-testid="agent-max-kb" value={form.max_kb_chars} onChange={(e) => setForm((f) => ({ ...f, max_kb_chars: Number(e.target.value) }))} className="rounded-md border border-input bg-background px-2 py-1 text-xs">
                    {[4000, 8000, 12000, 16000, 20000].map((n) => <option key={n} value={n}>{n.toLocaleString("it-IT")} caratteri</option>)}
                  </select>
                </label>
              )}
            </div>
          </div>
          <Toggle testid="agent-usa-kb" checked={form.usa_kb} onChange={(v) => setForm((f) => ({ ...f, usa_kb: v }))} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="agent-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 spin-slow" /> : <Save className="h-4 w-4" />} {dirty ? "Salva e applica all'agente" : "Riapplica all'agente"}
        </button>
        <button data-testid="agent-preview-btn" onClick={() => setPreview((p) => !p)} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm text-muted-foreground hover:text-white">
          {preview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {preview ? "Nascondi" : "Anteprima prompt"} · {data.caratteri_totali.toLocaleString("it-IT")} caratteri
        </button>
        {data.applicato_il && <span data-testid="agent-applied-at" className="text-xs text-muted-foreground">Applicato il {new Date(data.applicato_il).toLocaleString("it-IT")}</span>}
      </div>
      <p className="flex items-start gap-2 text-xs text-muted-foreground"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Se aggiorni la Knowledge Base o cambi contesto, premi di nuovo «Riapplica» per aggiornare l'agente. L'anteprima mostra sempre i dati attuali.</p>

      {preview && (
        <pre data-testid="agent-prompt-preview" className="max-h-[480px] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-background/70 p-4 font-mono text-[12px] leading-relaxed text-foreground/90">{data.anteprima}</pre>
      )}
    </section>
  );
};
